import React, { useState, useEffect, useRef, useCallback } from 'react';
import { consoleStream } from '../api';
import { getViewContext, subscribeViewContext } from '../lib/viewContext';

// The ~ console — Counter-Strike-style drop-down agentic surface. Press `
// anywhere in the CRM (outside a text field) and talk to a Claude tool loop
// over the live platform (squish-crm-backend lib/console-agent.js).
// Classic pages stay the primary UI; this is the opt-in fast path.
//
// Everything streams. The agent's reasoning, each tool call the moment it is
// dispatched, and the answer token by token all arrive over SSE, so a command
// that takes fifteen seconds looks like fifteen seconds of visible work rather
// than a dead prompt that suddenly dumps output.
//
// The console also rides along with whatever page is open: pages publish what
// they are showing through lib/viewContext, and every command carries that with
// it, so "close this ticket" resolves against the record on screen.

export default function Console() {
  const [open, setOpen] = useState(false);
  // Lines live in a ref, mirrored into state for rendering. Streaming needs a
  // synchronous, index-stable store: a functional setState updater doesn't run
  // until React renders, so it can't hand back the index of the line it just
  // added — and tool lines are patched in place by index as they run.
  const linesRef = useRef([
    { kind: 'sys', text: 'chuck — squish crm console. knows the page you\'re on. ` or Esc to close, ^C to cancel.' },
  ]);
  const [lines, setLines] = useState(linesRef.current);
  const commit = useCallback(() => setLines(linesRef.current.slice()), []);
  const push = useCallback((line) => {
    linesRef.current.push(line);
    commit();
    return linesRef.current.length - 1;
  }, [commit]);
  const patch = useCallback((idx, text) => {
    const line = linesRef.current[idx];
    if (!line) return;
    linesRef.current[idx] = { ...line, text };
    commit();
  }, [commit]);
  // Token append: extend the trailing line when it's the same kind, else start
  // a new one. Keeps a streamed paragraph as one line rather than one per token.
  const appendTo = useCallback((kind, text) => {
    const arr = linesRef.current;
    const last = arr[arr.length - 1];
    if (last && last.kind === kind) arr[arr.length - 1] = { ...last, text: last.text + text };
    else arr.push({ kind, text });
    commit();
  }, [commit]);
  const reset = useCallback((next) => { linesRef.current = next; commit(); }, [commit]);
  // What the mounted pages say is on screen. Re-rendered so the context strip
  // above the input tracks navigation while the console sits open.
  const [view, setView] = useState(getViewContext);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  // Conversation history sent to the backend (user/assistant text turns only).
  const historyRef = useRef([]);
  // Up-arrow command recall.
  const cmdsRef = useRef([]);
  const cmdIdxRef = useRef(-1);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && open) { setOpen(false); return; }
      // ^C cancels the running command without closing the console.
      if (e.ctrlKey && (e.key === 'c' || e.key === 'C') && open && abortRef.current) {
        e.preventDefault();
        abortRef.current.abort();
        return;
      }
      if (e.code !== 'Backquote' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      // Never steal ` from a real text field — except our own console input,
      // where ` should close the console rather than type a backtick.
      if (typing && !(open && t === inputRef.current)) return;
      e.preventDefault();
      setOpen(o => !o);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => subscribeViewContext(setView), []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Elapsed counter — the console never looks frozen, even in the gap before
  // the model's first token.
  useEffect(() => {
    if (!busy) return undefined;
    const startedAt = Date.now();
    setElapsed(0);
    const id = setInterval(() => setElapsed((Date.now() - startedAt) / 1000), 100);
    return () => clearInterval(id);
  }, [busy]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines, busy]);

  const submit = useCallback(async () => {
    const cmd = input.trim();
    if (!cmd || busy) return;
    setInput('');
    cmdsRef.current.push(cmd);
    cmdIdxRef.current = -1;

    if (cmd === 'clear') {
      historyRef.current = [];
      reset([{ kind: 'sys', text: 'history cleared.' }]);
      return;
    }

    // Local introspection: show exactly what the pages are handing the agent.
    if (cmd === 'context') {
      const ctx = getViewContext();
      push({ kind: 'cmd', text: cmd });
      push({ kind: 'sys', text: ctx ? JSON.stringify(ctx, null, 2) : 'no page context published.' });
      return;
    }

    push({ kind: 'cmd', text: cmd });
    historyRef.current.push({ role: 'user', content: cmd });
    setBusy(true);

    // Tool lines are addressed by index because a turn can open several tool
    // blocks before any of them runs: names arrive in block order, dispatch
    // follows the same order, so a FIFO matches them up.
    const pendingTools = [];   // line indices, in the order the model opened them
    let runningTool = null;    // { idx, label } — the one currently executing

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    let reply = '';

    try {
      await consoleStream(historyRef.current, getViewContext(), (evt) => {
        switch (evt.type) {
          // *_start events are deliberately not rendered — appendTo opens the
          // line lazily on the first token, so a block that turns out to be
          // empty never leaves a blank row behind. The elapsed counter is what
          // covers the gap before the first token arrives.
          case 'thinking':
            appendTo('think', evt.text);
            break;
          case 'text':
            appendTo('out', evt.text);
            break;
          case 'tool_start':
            pendingTools.push({ idx: push({ kind: 'trace', text: `⌁ ${evt.tool}(…)` }), tool: evt.tool });
            break;
          case 'tool': {
            const next = pendingTools.shift();
            const label = `⌁ ${evt.tool}(${JSON.stringify(evt.input)})`;
            // Fall back to a fresh line if the model somehow dispatched a tool
            // we never saw open — better a duplicate line than a lost trace.
            const idx = next ? next.idx : push({ kind: 'trace', text: label });
            if (next) patch(idx, label);
            runningTool = { idx, label };
            break;
          }
          case 'tool_done':
            if (runningTool) {
              patch(runningTool.idx, `${runningTool.label} ${evt.ok ? '✓' : '✗'} ${evt.ms}ms`);
              runningTool = null;
            }
            break;
          case 'done':
            reply = evt.reply;
            break;
          default:
            break;
        }
      }, ctrl.signal);

      // Only the answer goes into history — thinking and traces are display.
      if (reply) historyRef.current.push({ role: 'assistant', content: reply });
      else historyRef.current.pop();
    } catch (err) {
      // Drop the failed turn so a retry doesn't double it in history.
      historyRef.current.pop();
      push({
        kind: 'err',
        text: err.name === 'AbortError' ? '^C — cancelled.' : err.message,
      });
    } finally {
      abortRef.current = null;
      setBusy(false);
      inputRef.current?.focus();
    }
  }, [input, busy, push, patch, appendTo, reset]);

  const onInputKey = (e) => {
    if (e.key === 'Enter') { submit(); return; }
    if (e.key === 'ArrowUp') {
      const cmds = cmdsRef.current;
      if (cmds.length === 0) return;
      e.preventDefault();
      cmdIdxRef.current = cmdIdxRef.current === -1 ? cmds.length - 1 : Math.max(0, cmdIdxRef.current - 1);
      setInput(cmds[cmdIdxRef.current]);
    } else if (e.key === 'ArrowDown') {
      const cmds = cmdsRef.current;
      if (cmdIdxRef.current === -1) return;
      e.preventDefault();
      cmdIdxRef.current += 1;
      if (cmdIdxRef.current >= cmds.length) { cmdIdxRef.current = -1; setInput(''); }
      else setInput(cmds[cmdIdxRef.current]);
    }
  };

  if (!open) return null;

  return (
    <div style={wrap}>
      <div ref={scrollRef} style={scroll}>
        {lines.map((line, i) => (
          <div key={i} style={{ ...lineBase, ...styleFor(line.kind) }}>
            {line.kind === 'cmd' ? `> ${line.text}` : line.text}
          </div>
        ))}
        {busy && (
          <div style={{ ...lineBase, color: 'var(--text3)' }}>
            <span style={{ color: 'var(--accent3)' }}>▍</span> {elapsed.toFixed(1)}s
            <span style={{ opacity: 0.5 }}> · ^C to cancel</span>
          </div>
        )}
      </div>
      {view?.breadcrumb?.length > 0 && (
        <div style={contextRow} title="Sent with every command — type `context` to see the full payload">
          <span style={{ color: 'var(--accent3)', opacity: 0.7 }}>⌖</span>
          <span style={{ color: 'var(--text3)' }}>{view.breadcrumb.join(' › ')}</span>
        </div>
      )}
      <div style={inputRow}>
        <span style={{ color: 'var(--accent3)', marginRight: 8 }}>&gt;</span>
        <input
          ref={inputRef}
          style={inputStyle}
          value={input}
          disabled={busy}
          placeholder={busy ? 'working… ^C to cancel' : 'ask anything — e.g. open tickets for jane@acme.com'}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onInputKey}
        />
      </div>
    </div>
  );
}

function styleFor(kind) {
  switch (kind) {
    case 'cmd':   return { color: 'var(--accent3)' };
    case 'trace': return { color: 'var(--text3)', fontSize: 11 };
    // Reasoning is context, not output — dimmed so the answer still reads as
    // the answer when you scroll back.
    case 'think': return { color: 'var(--text3)', fontSize: 11, fontStyle: 'italic', opacity: 0.75 };
    case 'err':   return { color: 'var(--accent2)' };
    case 'sys':   return { color: 'var(--text3)', fontStyle: 'italic' };
    default:      return { color: 'var(--text)' };
  }
}

const wrap = {
  position: 'fixed', top: 0, left: 0, right: 0, height: '46vh', zIndex: 2000,
  display: 'flex', flexDirection: 'column',
  background: 'rgba(10, 10, 15, 0.94)', backdropFilter: 'blur(6px)',
  borderBottom: '1px solid var(--accent)', boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
  fontFamily: 'var(--mono)', fontSize: 13,
  // The console is a terminal — it stays dark in both themes. Pin the palette
  // to the dark values here so text/input/traces don't flip to dark-on-dark
  // (and vanish) when the CRM is in light mode.
  '--text': '#e8e8f0',
  '--text3': '#5a5a72',
  '--accent2': '#fa6d8a',
  '--accent3': '#6dfabc',
  '--border': '#2a2a38',
};
const scroll   = { flex: 1, overflowY: 'auto', padding: '14px 18px 6px' };
const lineBase = { whiteSpace: 'pre-wrap', lineHeight: 1.55, marginBottom: 2 };
const contextRow = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 18px 0',
  fontSize: 11, letterSpacing: '0.02em', whiteSpace: 'nowrap',
  overflow: 'hidden', textOverflow: 'ellipsis',
};
const inputRow = { display: 'flex', alignItems: 'center', padding: '8px 18px 12px', borderTop: '1px solid var(--border)' };
const inputStyle = {
  flex: 1, background: 'transparent', border: 'none', outline: 'none',
  color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 13,
};
